import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { Edit, Trash, Eye } from 'lucide-react';
import { Switch } from '@heroui/react';
import { ItemTypes } from './types';
import * as S from './CurriculumTabContent.styles';
/**
 * 드래그 가능한 강의 아이템 컴포넌트
 * 
 * 개별 강의를 표시하고 드래그 앤 드롭, 편집, 삭제 기능을 제공
 */ export const DraggableLesson = ({ lesson, lessonIndex, sectionId, isSelected, editingLessonSectionId, editingLessonId, editingLessonTitleCache, onMove, onLessonSelect, onStartEditLesson, onDeleteLesson, onSubmitEditLesson, onSetEditingLessonTitle, onTogglePreview, send })=>{
    const ref = useRef(null);
    // 드래그 설정
    const [{ isDragging }, drag] = useDrag({
        type: ItemTypes.LESSON,
        item: {
            type: ItemTypes.LESSON,
            id: lesson.id,
            index: lessonIndex,
            sectionId
        },
        collect: (monitor)=>({
                isDragging: monitor.isDragging()
            })
    });
    // 드롭 설정
    const [, drop] = useDrop({
        accept: ItemTypes.LESSON,
        hover: (item, monitor)=>{
            if (!ref.current) return;
            if (item.sectionId !== sectionId) return; // 같은 섹션 내에서만 이동 가능
            const dragIndex = item.index;
            const hoverIndex = lessonIndex;
            if (dragIndex === hoverIndex) return;
            // 머신에 이동 이벤트 전송
            send({
                type: 'MOVE_LESSON',
                sectionId,
                dragIndex,
                hoverIndex
            });
            item.index = hoverIndex;
        }
    });
    drag(drop(ref));
    // 강의 선택 핸들러
    const handleLessonClick = ()=>{
        onLessonSelect(sectionId, lesson.id);
    };
    // 편집 시작 핸들러
    const handleEditClick = (e)=>{
        e.stopPropagation();
        onStartEditLesson(sectionId, lesson.id);
    };
    // 삭제 핸들러
    const handleDeleteClick = (e)=>{
        e.stopPropagation();
        onDeleteLesson(sectionId, lesson.id);
    };
    // 편집 제목 변경 핸들러
    const handleTitleChange = (e)=>{
        onSetEditingLessonTitle(e.target.value);
    };
    // 편집 폼 제출 핸들러
    const handleFormSubmit = (e)=>{
        e.stopPropagation();
        onSubmitEditLesson(e);
    };
    // 편집 중인지 확인
    const isEditing = editingLessonSectionId === sectionId && editingLessonId === lesson.id;
    // 무료 미리보기 토글 핸들러
    const handlePreviewToggle = (e)=>{
        e.stopPropagation();
        if (onTogglePreview) {
            onTogglePreview(sectionId, lesson.id, e.target.checked);
        }
    };
    return /*#__PURE__*/ _jsxs(S.LessonItem, {
        ref: ref,
        $isSelected: isSelected,
        $isDragging: isDragging,
        onClick: handleLessonClick,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                style: {
                    display: 'flex',
                    alignItems: 'center',
                    flexGrow: 1
                },
                children: [
                    /*#__PURE__*/ _jsx(S.DragHandle, {
                        children: "≡"
                    }),
                    isEditing ? /*#__PURE__*/ _jsx("form", {
                        onSubmit: handleFormSubmit,
                        onClick: (e)=>e.stopPropagation(),
                        style: {
                            flexGrow: 1
                        },
                        children: /*#__PURE__*/ _jsx(S.InputField, {
                            value: editingLessonTitleCache,
                            onChange: handleTitleChange,
                            autoFocus: true,
                            onBlur: onSubmitEditLesson
                        })
                    }) : /*#__PURE__*/ _jsx(S.LessonTitle, {
                        children: lesson.title
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(S.LessonActions, {
                "data-lesson-actions": "true",
                onClick: (e)=>e.stopPropagation(),
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        style: {
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginRight: '8px'
                        },
                        title: "이 레슨을 무료 미리보기로 공개",
                        children: [
                            /*#__PURE__*/ _jsx(Eye, {
                                size: 14,
                                style: {
                                    color: lesson.isPreview ? '#22c55e' : '#94a3b8'
                                }
                            }),
                            /*#__PURE__*/ _jsx(Switch, {
                                size: "sm",
                                isSelected: lesson.isPreview ?? false,
                                onChange: handlePreviewToggle,
                                "aria-label": "무료 미리보기",
                                "data-testid": "curriculum-lesson-preview-toggle",
                                classNames: {
                                    base: 'shadow-none',
                                    wrapper: 'shadow-none',
                                    thumb: 'shadow-none'
                                }
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(S.ActionButton, {
                        onClick: handleEditClick,
                        title: "강의 이름 수정",
                        children: /*#__PURE__*/ _jsx(Edit, {
                            size: 14
                        })
                    }),
                    /*#__PURE__*/ _jsx(S.ActionButton, {
                        onClick: handleDeleteClick,
                        title: "강의 삭제",
                        children: /*#__PURE__*/ _jsx(Trash, {
                            size: 14
                        })
                    })
                ]
            })
        ]
    });
};
